import type { Revision } from '@mathflat/domain/SchoolSystem'
import type { MonthLabelExpandedValue, YEAR_LABEL_EXPANDED } from 'src/constant'
import {
  DEPARTMENT_LABEL,
  DEPARTMENT_LABEL_EXPANDED,
  DEPARTMENT_SUBJECT_LABEL_EXPANDED,
  SUBJECT_LABEL,
} from 'src/constant'
import { ReqParams } from 'src/dto'
import type { PageParamT } from 'src/dto/common/ReqParams'

import commonRepo from '@/repository/common.repo'
import type { curriculumCoreService } from '@/service/curriculumCore.service'

export const WORKSHEET_PAIR_TYPE = ['PRACTICE_TEST_PAIR', 'PRACTICE_TEST_SIMILAR'] as const
type WorksheetPairType = (typeof WORKSHEET_PAIR_TYPE)[number]

type PracticeFilterParamT = {
  grade?: ReturnType<typeof curriculumCoreService.getUnionGradeHigh>[number]['gradeSubject']
  year: (typeof YEAR_LABEL_EXPANDED)[number]['value'] | null
  month?: MonthLabelExpandedValue
  department?: (typeof DEPARTMENT_LABEL_EXPANDED)[number]['value'] | null
  title?: string
  revision?: Revision
  type:
    | (typeof SUBJECT_LABEL)[number]['value']
    | (typeof DEPARTMENT_LABEL_EXPANDED)[number]['value']
    | null
    | 'ALL'
  worksheetTypes: ('PRACTICE_TEST' | WorksheetPairType)[] | null
} & PageParamT

export class PracticeTestWorksheetFilter extends ReqParams<PracticeFilterParamT> {
  constructor(data?) {
    const params: Pick<PracticeFilterParamT, 'year' | 'month' | 'type' | 'worksheetTypes'> = {
      year: null,
      month: null,
      type: null,
      worksheetTypes: null,
    }

    if (data?.params) {
      Object.assign(params, data.params)
    }

    super({
      params: params as PracticeFilterParamT,
      excludeParams: ['subject', 'department'] as (keyof PracticeFilterParamT)[],
    })

    if (commonRepo.isSolo && !this.params.type) {
      const targetType = this.getInitialType(this.departmentLabel)

      if (targetType) {
        this.params.type = targetType.value
      } else {
        this.params.type = 'LIBERAL'
      }
    }
  }
  getInitialType(departmentLabel: PracticeTestWorksheetFilter['departmentLabel']) {
    return departmentLabel.find((department) => department.subscribedStatus === 'SUBSCRIBED')
  }
  defaultLabelsByGrade(grade: PracticeFilterParamT['grade'], year?: PracticeFilterParamT['year']) {
    const _year = year || this.params.year
    // 고1은 공통만 존재
    if (grade === '1')
      return [{ label: '과목 전체', value: 'ALL', subscribedStatus: 'SUBSCRIBED' }] as const

    // 고2는 문/이과+공통만 존재
    if (grade === '2') {
      return commonRepo.isSolo
        ? this._addSubscribeStatus(DEPARTMENT_LABEL)
        : this._addSubscribeStatus(DEPARTMENT_LABEL_EXPANDED)
    }
    if (!_year) {
      if (commonRepo.isSolo)
        return [
          ...this._addSubscribeStatus(SUBJECT_LABEL),
          ...this._addSubscribeStatus(DEPARTMENT_LABEL),
        ]
      return this._addSubscribeStatus(DEPARTMENT_SUBJECT_LABEL_EXPANDED)
    }

    // 문/이과는 2020년 까지만 존재함
    if (Number(_year) > 2020) {
      if (commonRepo.isSolo) return this._addSubscribeStatus(SUBJECT_LABEL)
      return this._addSubscribeStatus([{ label: '과목 전체', value: 'ALL' }, ...SUBJECT_LABEL])
    }

    if (commonRepo.isSolo) {
      return [...this._addSubscribeStatus(DEPARTMENT_LABEL)]
    }

    return this._addSubscribeStatus(DEPARTMENT_SUBJECT_LABEL_EXPANDED)
  }

  private _addSubscribeStatus(list) {
    return list.map((item) => {
      if ('trieKey' in item) {
        return {
          ...item,
          subscribedStatus: commonRepo.permission.bySubscription.validateSubscription({
            trieKey: item.trieKey,
          })
            ? 'SUBSCRIBED'
            : 'UNSUBSCRIBED',
        }
      }
      return {
        ...item,
        subscribedStatus: 'SUBSCRIBED',
      }
    })
  }

  get departmentLabel() {
    return this.defaultLabelsByGrade(this.params.grade)
  }

  get worksheetTypeFilterValue(): WorksheetPairType | null {
    const pairTypes = this.params.worksheetTypes?.filter(
      (t): t is WorksheetPairType => t === 'PRACTICE_TEST_PAIR' || t === 'PRACTICE_TEST_SIMILAR',
    )
    if (!pairTypes || pairTypes.length !== 1) return null
    return pairTypes[0]
  }
}
